import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Loading } from 'components';
import { Button, Icon } from 'design-system-web';
import { currencyToPaymentCurrency } from 'domains-billing-web-mappers';
import { useChargeManager } from 'lane-shared/domains/billingPayments/hooks';
import {
  Charge,
  ExternalEntityType,
  ExternalPayerType,
} from 'lane-shared/domains/billingPayments/types';
import { UserType } from 'lane-shared/types/User';
import { useBpAdminFeeChargeCodeEnabled } from 'react-hooks';

import useUserLocale from 'hooks/useUserLocale';

import { AddProductFormModal } from 'domains/billingAndPayments/components/ChargeManager/AddProductFormModal';
import { ChargeSummary } from 'domains/billingAndPayments/components/ChargeManager/ChargeSummary/ChargeSummary';
import { ChargesList } from 'domains/billingAndPayments/components/ChargeManager/ChargesList/ChargesList';
import { DraftChargeConfirmationModal } from 'domains/billingAndPayments/components/ChargeManager/DraftChargeConfirmationModal/DraftChargeConfirmationModal';
import { DraftItemsTable } from 'domains/billingAndPayments/components/ChargeManager/DraftItemsTable/DraftItemsTable';

import { useInvoiceOptionsForChannel } from '../../verifyAndSubmit/useInvoiceOptionsForChannel';
import { useFocusOnSubmittedCharge } from '../../verifyAndSubmit/useFocusOnSubmittedCharge';
import { useReloadedSubmittedCharge } from '../../verifyAndSubmit/useReloadedSubmittedCharge';
import { useVerifyAndSubmitDraft } from '../../verifyAndSubmit/useVerifyAndSubmitDraft';
import { createVerifyAndSubmitViewModel } from './createVerifyAndSubmitViewModel';

import styles from './VerifyAndSubmitBilling.scss';

type Props = {
  channel: { _id: string; slug?: string };
  user: UserType;
  canVerifyAndSubmit: boolean;
  isServiceRequestComplete: boolean;
  externalPayerId: string;
  externalPayerType: ExternalPayerType;
  externalEntityType: ExternalEntityType;
  externalEntityId: string;
  chargeName: string;
  chargeDescription?: string;
  metadata?: Record<string, unknown>;
  onSubmitCharge?: (charge: Charge) => void;
};

export function VerifyAndSubmitBilling({
  channel,
  user,
  canVerifyAndSubmit,
  isServiceRequestComplete,
  externalPayerId,
  externalPayerType,
  externalEntityType,
  externalEntityId,
  chargeName,
  chargeDescription,
  metadata,
  onSubmitCharge,
}: Props) {
  const { t } = useTranslation();
  const locale = useUserLocale();
  const isAdminFeeFeatureEnabled = useBpAdminFeeChargeCodeEnabled();
  const {
    loading: isInvoiceOptionsLoading,
    currency,
    invoiceDueInDays,
    invoiceType,
    invoiceDueDate,
    isInvoiceOptionEnabled,
    showSRChargeStatus,
    updateChargeDetails,
  } = useInvoiceOptionsForChannel(channel._id);
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const {
    charges,
    draftCharge,
    loading,
    validation,
    voidCharge,
    addDraftProductItem,
    updateDraftProductItem,
    deleteDraftProductItem,
  } = useChargeManager({
    channel,
    actingUser: user,
    externalPayerId,
    externalPayerType,
    currency,
    externalEntityId,
    externalEntityType,
    invoiceType,
    locale,
    chargeName,
    chargeDescription,
    invoiceDueInDays,
    metadata,
    isAdminFeeFeatureEnabled,
  });

  const { submit, retry, submittedCharge, isSubmitting, failure } =
    useVerifyAndSubmitDraft({
      draftCharge,
      chargeCount: charges.length,
      request: {
        chargeName,
        chargeDescription,
        externalPayerId,
        externalPayerType,
        externalEntityId,
        externalEntityType,
        channelId: channel._id,
        actingUserId: user._id,
        currency,
        locale,
        invoiceType,
        invoiceDueInDays,
        metadata: metadata ?? {},
        isAdminFeeFeatureEnabled,
      },
      onSubmitCharge,
    });
  const allCharges = useReloadedSubmittedCharge({
    charges,
    submittedCharge,
    channelId: channel._id,
    externalEntityId,
    externalEntityType,
  });

  const {
    showEditor,
    isEditorReadOnly,
    showVerifyButton,
    isVerifyDisabled,
    showIncompleteServiceRequestHint,
    showRetryButton,
    isRetryDisabled,
    failureMessage,
  } = createVerifyAndSubmitViewModel({
    charges: allCharges,
    itemCount: draftCharge.items.length,
    canVerifyAndSubmit,
    isServiceRequestComplete,
    hasResolvedIds: [
      draftCharge.id,
      externalEntityId,
      channel._id,
      externalPayerId,
      user._id,
    ].every(Boolean),
    isSubmitting,
    validation,
    failure,
  });

  const failureAlertRef = useRef<HTMLDivElement>(null);
  const chargesRegionRef = useFocusOnSubmittedCharge(submittedCharge?.id);

  useEffect(() => {
    if (failure) {
      failureAlertRef.current?.focus();
    }
  }, [failure]);

  const handleConfirm = async () => {
    await submit({ invoiceDueDate });
    setIsConfirmOpen(false);
  };

  if (loading || isInvoiceOptionsLoading) {
    return (
      <Flex p={4} width="full" justify="center" align="center">
        <Loading />
      </Flex>
    );
  }

  const paymentCurrency = currencyToPaymentCurrency(currency);

  const addProductButton = (
    <Button
      startIcon={<Icon name="Plus" />}
      className={styles.AddProductButton}
      variant="tertiary"
      disabled={isEditorReadOnly}
      onClick={() => setAddProductModalOpen(true)}
      testId="add-product-button"
    >
      {t('abp.productsServices.table.add')}
    </Button>
  );

  return (
    <Flex gap={4} className={styles.VerifyAndSubmitBilling} direction="column">
      {showEditor && (
        <>
          <Flex className={styles.DraftCharge} gap={5}>
            <DraftItemsTable
              items={draftCharge.items}
              locale={locale}
              addProductButton={addProductButton}
              updateDraftProductItem={updateDraftProductItem}
              deleteDraftProductItem={deleteDraftProductItem}
              readOnly={isEditorReadOnly}
              currency={paymentCurrency}
              channelId={channel._id}
            />
            <ChargeSummary
              items={draftCharge.items}
              locale={locale}
              currency={paymentCurrency}
            >
              <Flex direction="column" gap={3}>
                {failureMessage && (
                  <div
                    ref={failureAlertRef}
                    className={styles.FailureMessage}
                    role="alert"
                    tabIndex={-1}
                  >
                    {t(failureMessage.key, {
                      chargeId: failureMessage.chargeId,
                    })}
                  </div>
                )}
                {showIncompleteServiceRequestHint && (
                  <div className={styles.Hint}>
                    {t(
                      'abp.charges.verifyAndSubmit.serviceRequestNotComplete.hint'
                    )}
                  </div>
                )}
                {showVerifyButton && (
                  <Button
                    variant="primary"
                    className={styles.VerifyAndSubmitButton}
                    testId="verify-and-submit-button"
                    disabled={isVerifyDisabled}
                    onClick={() => setIsConfirmOpen(true)}
                  >
                    {t('abp.charges.verifyAndSubmit.submitButton')}
                  </Button>
                )}
                {showRetryButton && (
                  <Button
                    variant="primary"
                    className={styles.VerifyAndSubmitButton}
                    testId="retry-verification-button"
                    disabled={isRetryDisabled}
                    loading={isSubmitting}
                    onClick={retry}
                  >
                    {t('abp.charges.verifyAndSubmit.retryButton')}
                  </Button>
                )}
              </Flex>
            </ChargeSummary>
          </Flex>
          <AddProductFormModal
            onAddProduct={addDraftProductItem}
            currency={paymentCurrency}
            isOpen={addProductModalOpen}
            onClose={() => setAddProductModalOpen(false)}
            payerId={externalPayerId}
            payerType={externalPayerType}
            testId="add-product-form-modal"
          />
          <DraftChargeConfirmationModal
            isOpen={isConfirmOpen}
            updateChargeDetails={updateChargeDetails}
            onClose={() => setIsConfirmOpen(false)}
            handleConfirm={handleConfirm}
            cancelText={t(
              'abp.charges.chargeManager.draftCharge.submitModal.cancelButton'
            )}
            confirmText={t('abp.charges.verifyAndSubmit.modal.confirmButton')}
            title={t('abp.charges.verifyAndSubmit.modal.title')}
            description={t('abp.charges.verifyAndSubmit.modal.description')}
            isInvoiceOptionEnabled={isInvoiceOptionEnabled}
            invoiceType={invoiceType}
            confirmActionDisabled={isSubmitting}
            loading={isSubmitting}
          />
        </>
      )}
      {allCharges.length > 0 && (
        <div
          ref={chargesRegionRef}
          tabIndex={-1}
          className={styles.Charges}
          data-test="verify-and-submit-charges"
        >
          <ChargesList
            charges={allCharges}
            currency={paymentCurrency}
            locale={locale}
            voidCharge={voidCharge}
            showVoidOption={false}
            showSRChargeStatus={showSRChargeStatus}
            channelSlug={channel.slug}
          />
        </div>
      )}
    </Flex>
  );
}
